import { portalText, usePortalTranslation } from "../i18n/portal.js";
/**
 * The post-switch notice (plan-014 A4, FR12-AUTH-11): after the console moves to another
 * organization — because the person switched here, or another tab did — the page it lands on says so,
 * in FR12-AUTH-11's words: "Switched to {name}. Your previous organization's open pages and drafts
 * were closed." The prototype draws it as the toast on the destination page (`opToast`); here it is a
 * status line above the page, which a screen reader announces and the person can dismiss. It also
 * says where the console moved them when their workspace was archived or their organization suspended
 * (FR12-AUTH-12). It disappears on the next page they open.
 */
import { useRouterState } from "@tanstack/react-router";
import { Icon, IconButton } from "@wetalk/ui";
import { useEffect, useRef, type ReactNode } from "react";

import { GLYPH } from "./glyph.js";
import { scopeNoticeText, useScopeNotice } from "../scope/guard.js";

export function PostSwitchNotice(): ReactNode {
  usePortalTranslation("shell");
  const { notice, dismiss } = useScopeNotice();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  // The page the notice was shown on; opening another one retires it (the prototype clears its
  // toast on the next navigation).
  const shownOn = useRef<string | null>(null);

  useEffect(() => {
    if (notice === null) {
      shownOn.current = null;
      return;
    }
    if (shownOn.current === null) {
      shownOn.current = pathname;
      return;
    }
    if (shownOn.current !== pathname) dismiss();
  }, [notice, pathname, dismiss]);

  if (notice === null) return null;
  return (
    <p className="c-post-switch" role="status">
      <Icon glyph={GLYPH.checkCircle} size="lg" />
      <span className="c-post-switch-text">{scopeNoticeText(notice)}</span>
      <IconButton glyph={GLYPH.x} label={portalText("shell", "dismiss_70afe9ef")} title={portalText("shell", "dismiss_70afe9ef")} size="md" onClick={dismiss} />
    </p>
  );
}
